import { useState } from "react";
import type { FormEvent } from "react";

import type { TeamMemberUpdateVariables } from "../Pages/TeamMember/TeamMemberEdit/TeamMemberUpdateMutation";
import { Input, Textarea } from "../base";
import type { InputProps } from "../base";
import type { TeamMemberEditData } from "../Pages/TeamMember/TeamMemberEdit/TeamMemberEditQuery";
import type { TeamMemberCreateVariables } from "../Pages/TeamMemberNew/TeamMemberCreateMutation";

import * as styles from "./TeamMemberForm.styles";

interface Props {
  onCreate?: (teamMember: TeamMemberCreateVariables) => Promise<void>;
  onUpdate?: (teamMember: TeamMemberUpdateVariables) => Promise<void>;
  teamMember?: TeamMemberEditData["teamMember"];
}

const TeamMemberForm = ({ onCreate, onUpdate, teamMember }: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<
    TeamMemberUpdateVariables | TeamMemberCreateVariables | null
  >(teamMember ?? null);

  const isEditing = !!teamMember;

  const renderHeader = () => {
    if (isEditing) {
      return <h2>Editing: {teamMember?.name}</h2>;
    }

    return <h2>New Team Member</h2>;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    if (formData) {
      if (teamMember) {
        await onUpdate?.(formData as TeamMemberUpdateVariables);
      } else {
        await onCreate?.(formData as TeamMemberCreateVariables);
      }
    }

    setIsSaving(false);
  };

  const handleChange: InputProps["onChange"] = (name, value) => {
    if (!name) return;

    setFormData({ ...(formData || { id: "" }), [name]: value });
  };

  return (
    <>
      {renderHeader()}
      <form onSubmit={handleSubmit}>
        <Input
          placeholder="Name"
          label="Name"
          required
          name="name"
          id="name"
          value={formData?.name}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="text"
          required
          placeholder="Position"
          label="Position"
          name="position"
          id="position"
          value={formData?.position ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="tel"
          required
          placeholder="Phone: xxx-xxx-xxxx"
          label="Phone"
          name="phone"
          id="phone"
          value={formData?.phone ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="email"
          required
          placeholder="Email"
          label="Email"
          name="email"
          id="email"
          value={formData?.email ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="number"
          placeholder="Display Order"
          label="Display Order"
          name="order"
          id="order"
          value={formData?.order ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="text"
          required
          placeholder="Listing Agent Key (can get this from realtor.ca URL)"
          label="Listing Agent Key"
          name="listingAgentKey"
          id="listingAgentKey"
          value={(formData?.listingAgentKey || teamMember?.listingAgent.key) ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <input
          type="file"
          id="avatar"
          aria-label="Avatar"
          name="avatar"
          onChange={({ target: { files } }) => {
            setFormData({
              ...(formData || { id: "" }),
              avatar: files?.[0] as File,
            });
          }}
        />
        <Textarea
          placeholder="Description"
          id="description"
          name="description"
          value={formData?.description ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <button
          css={styles.submitButton}
          className="btn btn-large"
          type="submit"
          disabled={isSaving}
        >
          Send
        </button>
      </form>
    </>
  );
};

export { TeamMemberForm, type Props };
